import type { MessageKey } from '@/shared/i18n';
import type {
  ActorType,
  CurrentUser,
} from '@/features/identity-management/auth/models/auth.types';
import { ROUTES } from '@/app/router/route-paths';

export type { ActorType };

export interface AppNavItem {
  key: string;
  labelKey: MessageKey;
  descriptionKey: MessageKey;
  /** Route literal — sourced from ROUTES in route-paths.ts. */
  to?: string;
  /** A fully shipped, real-data module. */
  isLive: boolean;
  /**
   * A reserved PRD module that is navigable as a "what's this feature"
   * preview but is NOT a live feature. Keeps `isLive: false` semantics while
   * still rendering a working link (and its "Soon"/"Next" chip).
   */
  isPreview?: boolean;
  requiredActorTypes?: ActorType[];
  requiredPermission?: string;
  chipKey?: MessageKey;
}

export const appNavItems: AppNavItem[] = [
  // ---- Candidate ----
  {
    key: 'workspace',
    labelKey: 'appShell.nav.workspace',
    descriptionKey: 'appShell.nav.workspace.description',
    to: ROUTES.app.root,
    isLive: true,
    requiredActorTypes: ['candidate'],
  },
  // Profile + Smart Profile are consolidated: Smart Profile is the single
  // candidate profile surface in the rail; the editable profile form lives at
  // ROUTES.app.profile and is reached via the "Edit profile" button on Smart
  // Profile (titled through EXTRA_TITLES below, not the rail).
  {
    key: 'smart-profile',
    labelKey: 'appShell.nav.smartProfile',
    descriptionKey: 'appShell.nav.smartProfile.description',
    to: ROUTES.app.smartProfile,
    isLive: true,
    requiredActorTypes: ['candidate'],
  },
  {
    key: 'job-matches',
    labelKey: 'appShell.nav.jobMatches',
    descriptionKey: 'appShell.nav.jobMatches.description',
    to: ROUTES.app.jobMatches,
    isLive: true,
    requiredActorTypes: ['candidate'],
  },
  {
    key: 'applications',
    labelKey: 'appShell.nav.applications',
    descriptionKey: 'appShell.nav.applications.description',
    to: ROUTES.app.applications,
    isLive: true,
    requiredActorTypes: ['candidate'],
  },

  // ---- Employer ----
  {
    key: 'employer-dashboard',
    labelKey: 'appShell.nav.employerDashboard',
    descriptionKey: 'appShell.nav.employerDashboard.description',
    to: ROUTES.app.employer,
    isLive: true,
    requiredActorTypes: ['employer'],
  },
  {
    key: 'company-profile',
    labelKey: 'appShell.nav.companyProfile',
    descriptionKey: 'appShell.nav.companyProfile.description',
    to: ROUTES.app.companyProfile,
    isLive: true,
    requiredActorTypes: ['employer'],
  },
  {
    key: 'jobs',
    labelKey: 'appShell.nav.jobs',
    descriptionKey: 'appShell.nav.jobs.description',
    to: ROUTES.app.company,
    isLive: true,
    requiredActorTypes: ['employer'],
  },
  {
    key: 'employer-applications',
    labelKey: 'appShell.nav.employerApplications',
    descriptionKey: 'appShell.nav.employerApplications.description',
    to: ROUTES.app.companyApplications,
    isLive: true,
    requiredActorTypes: ['employer'],
  },
  {
    key: 'find-candidates',
    labelKey: 'appShell.nav.findCandidates',
    descriptionKey: 'appShell.nav.findCandidates.description',
    to: ROUTES.app.companyCandidates,
    isLive: true,
    requiredActorTypes: ['employer'],
  },

  // ---- Admin (each gated by its OWN permission; the super-admin holds all) ----
  {
    key: 'admin-dashboard',
    labelKey: 'appShell.nav.adminDashboard',
    descriptionKey: 'appShell.nav.adminDashboard.description',
    to: ROUTES.app.admin,
    isLive: true,
    requiredPermission: 'metrics.read',
  },
  {
    key: 'admin-users',
    labelKey: 'appShell.nav.adminUsers',
    descriptionKey: 'appShell.nav.adminUsers.description',
    to: ROUTES.app.adminUsers,
    isLive: true,
    requiredPermission: 'users.read',
  },
  {
    key: 'admin-roles',
    labelKey: 'appShell.nav.adminRoles',
    descriptionKey: 'appShell.nav.adminRoles.description',
    to: ROUTES.app.adminRoles,
    isLive: true,
    requiredPermission: 'roles.read',
  },
  {
    key: 'admin-plans',
    labelKey: 'appShell.nav.adminPlans',
    descriptionKey: 'appShell.nav.adminPlans.description',
    to: ROUTES.app.adminPlans,
    isLive: true,
    requiredPermission: 'plans.read',
  },
  {
    key: 'admin-ai-usage',
    labelKey: 'appShell.nav.adminAiUsage',
    descriptionKey: 'appShell.nav.adminAiUsage.description',
    to: ROUTES.app.adminAiUsage,
    isLive: true,
    requiredPermission: 'metrics.read',
  },
  {
    key: 'admin-early-access',
    labelKey: 'appShell.nav.adminEarlyAccess',
    descriptionKey: 'appShell.nav.adminEarlyAccess.description',
    to: ROUTES.app.adminEarlyAccess,
    isLive: true,
    requiredPermission: 'early-access.read',
  },
  {
    key: 'admin-settings',
    labelKey: 'appShell.nav.adminSettings',
    descriptionKey: 'appShell.nav.adminSettings.description',
    to: ROUTES.app.adminSettings,
    isLive: true,
    requiredPermission: 'settings.read',
  },
  {
    key: 'admin-audit-logs',
    labelKey: 'appShell.nav.adminAuditLogs',
    descriptionKey: 'appShell.nav.adminAuditLogs.description',
    to: ROUTES.app.adminAuditLogs,
    isLive: true,
    requiredPermission: 'audit-logs.read',
  },
];

/** Route paths not present in the nav rail, but still titled in the top bar. */
const EXTRA_TITLES: { path: string; key: MessageKey }[] = [
  { path: ROUTES.app.profile, key: 'appShell.nav.profile' },
  { path: ROUTES.app.account, key: 'account.title' },
  { path: ROUTES.app.settings, key: 'appShell.nav.settings' },
  { path: ROUTES.app.analytics, key: 'appShell.topbar.tab.analytics' },
  { path: ROUTES.app.integrations, key: 'appShell.topbar.tab.integrations' },
];

/**
 * Resolves the i18n key for the current page title from a pathname — exact
 * match first, then longest path-prefix. Returns null when nothing matches so
 * the caller can fall back to the app name.
 */
export function resolvePageTitleKey(pathname: string): MessageKey | null {
  const candidates: { path: string; key: MessageKey }[] = [
    ...appNavItems
      .filter((item) => item.to)
      .map((item) => ({
        path: (item.to as string).split('#')[0] as string,
        key: item.labelKey,
      })),
    ...EXTRA_TITLES,
  ];

  const exact = candidates.find((c) => c.path === pathname);
  if (exact) return exact.key;

  const byLength = [...candidates].sort((a, b) => b.path.length - a.path.length);
  const prefixed = byLength.find(
    (c) => pathname === c.path || pathname.startsWith(`${c.path}/`),
  );
  return prefixed ? prefixed.key : null;
}

export function filterAppNavItems(user: CurrentUser | null): AppNavItem[] {
  // A not-yet-onboarded (null) account defaults to the candidate workspace, so
  // it sees the candidate modules rather than an empty rail. Recruiters never
  // reach the shell (RequireWorkspaceAccess), so their nav is moot.
  const actorType: ActorType = user?.primaryActorType ?? 'candidate';

  return appNavItems.filter((item) => {
    if (
      item.requiredActorTypes &&
      !item.requiredActorTypes.includes(actorType)
    ) {
      return false;
    }

    if (item.requiredPermission && !user?.permissions.includes(item.requiredPermission)) {
      return false;
    }

    return true;
  });
}
